/**
 * `<DiagrammeCorrigeWeb>` — un DIAGRAMME STATISTIQUE CORRIGÉ (`CAP-08`,
 * tranche T8), dans « Travaux et corrections » : le diagramme tel que l'élève
 * l'a construit, puis chaque élément qu'il devait donner, avec ce qu'il est
 * devenu. Jamais la couleur seule : chaque élément porte un mot, et la valeur
 * attendue quand il est à reprendre.
 *
 * Atelier de figures, L5 : une droite tracée à l'œil se montre avec la BANDE
 * acceptée et le point moyen ; un graphe, avec le parcours numéroté et l'arête
 * fautive désignée (`resoudreDiagrammeCorrige`, le même dessin que l'élève).
 * Ce que le code a établi suit, constat par constat.
 */
import {
  MOTS_CRITERE_LU,
  ecrireSaisie,
  resoudreDiagrammeCorrige,
  type DiagrammeCorrigeDto,
  type EtatDElement,
} from '@silex/shared';
import { useMemo, type ReactNode } from 'react';

import { DessinWeb } from './DessinWeb';

const MOT: Readonly<Record<EtatDElement, string>> = {
  donnee: '',
  juste: 'juste',
  a_revoir: 'à revoir',
  vide: 'rien donné',
};

export function DiagrammeCorrigeWeb({
  corrige,
  testid = 'diagramme-corrige',
}: {
  readonly corrige: DiagrammeCorrigeDto;
  readonly testid?: string;
}): ReactNode {
  const resolu = useMemo(() => resoudreDiagrammeCorrige(corrige), [corrige]);
  return (
    <div className="diagramme-corrige" data-testid={testid}>
      <DessinWeb resolu={resolu} largeurMax={560} testid={`${testid}-dessin`} />
      <ul className="diagramme-corrige__elements">
        {corrige.elements
          .filter((e) => e.etat !== 'donnee')
          .map((e, i) => (
            <li
              key={i}
              className={`diagramme-corrige__element diagramme-corrige__element--${e.etat}`}
            >
              {e.nom} : {e.valeur === null ? '—' : ecrireSaisie(e.valeur)} — {MOT[e.etat]}
              {e.attendu === undefined ? '' : ` (attendu : ${e.attendu})`}
            </li>
          ))}
        {(corrige.constats ?? []).map((k, i) => (
          <li
            key={`constat-${i}`}
            className={`diagramme-corrige__element diagramme-corrige__element--${k.etat === 'tenu' ? 'juste' : 'a_revoir'}`}
          >
            {k.etat === 'tenu' ? MOTS_CRITERE_LU.tenu : MOTS_CRITERE_LU.aRevoir} — {k.texte}
          </li>
        ))}
      </ul>
    </div>
  );
}
